import { createStore } from 'react-sweet-state';
import { cloneDeep } from 'lodash';

import { AI_SUGGESTION_MODE, getAlignedSuggestionMode } from 'src/data/generation';
import {
  getToolGenerationModes,
  isFreestyleGenerationTool,
  isMixtureTool,
} from 'src/data/generationTool';
import { filterVisibleDDTemplates } from 'src/data/utils';
import { getMultiPromptRunner, runMultiPromptRunnerAsync } from 'src/graphql/generation';
import { promisedWait } from 'src/lib/common';

const INITIAL_STATE = {
  generationTool: null,
  chainId: null,
  csvChainId: null,
  removedBlocks: [],
  toolPrompts: [],
};

export const PRIVATE_ACTIONS = {
  init:
    (generationTool) =>
    ({ setState }) => {
      setState({
        ...cloneDeep(INITIAL_STATE),
        generationTool,
      });
    },

  prepareToolPromptForGeneration:
    (toolPrompt, language, extractedBriefs) =>
    (_, { brandVoiceIdsForBrandTerms, brandRulesEnabled }) => {
      const toolPromptCopy = cloneDeep(toolPrompt);
      if (isMixtureTool(toolPromptCopy.generationTool)) {
        Object.keys(toolPromptCopy.subGenerationToolsProps).forEach((key) => {
          delete toolPromptCopy.subGenerationToolsProps[key].errors;
          delete toolPromptCopy.subGenerationToolsProps[key].isCustomFormulasVisible;
          delete toolPromptCopy.subGenerationToolsProps[key].visibleDDTemplates;
          toolPromptCopy.subGenerationToolsProps[key].suggestionMode = getAlignedSuggestionMode(
            toolPromptCopy.subGenerationToolsProps[key].selectedModeId,
            toolPromptCopy.subGenerationToolsProps[key].selectedModeType
          );
          toolPromptCopy.subGenerationToolsProps[key].customFormulaId =
            toolPromptCopy.subGenerationToolsProps[key].suggestionMode ===
            AI_SUGGESTION_MODE.customFormula
              ? toolPromptCopy.subGenerationToolsProps[key].selectedModeId
              : null;

          toolPromptCopy.subGenerationToolsProps[key].brandVoiceIdsForBrandTerms = brandRulesEnabled
            ? brandVoiceIdsForBrandTerms
            : [];
        });
        toolPromptCopy.suggestionMode = 'auto';
      } else {
        toolPromptCopy.suggestionMode = getAlignedSuggestionMode(
          toolPromptCopy.selectedModeId,
          toolPromptCopy.selectedModeType
        );
        toolPromptCopy.customFormulaId =
          toolPromptCopy.suggestionMode === AI_SUGGESTION_MODE.customFormula
            ? toolPromptCopy.selectedModeId
            : null;
        delete toolPromptCopy.selectedModeId;
        delete toolPromptCopy.selectedModeType;
        delete toolPromptCopy.visibleDDTemplates;

        toolPromptCopy.brandVoiceIdsForBrandTerms = brandRulesEnabled
          ? brandVoiceIdsForBrandTerms
          : [];
      }
      if (toolPromptCopy.generationCustomModelId) {
        toolPromptCopy.customFormulaId = null;
        toolPromptCopy.customFormulaName = null;
        toolPromptCopy.tov = null;
        toolPromptCopy.tovName = null;
        toolPromptCopy.customTovId = null;
      }
      toolPromptCopy.language = language;
      if (extractedBriefs) {
        toolPromptCopy.urlExtractions = Object.entries(extractedBriefs)
          .filter(([url, _]) => toolPromptCopy.prompt.includes(url))
          .map(([url, extractedBrief]) => ({
            url,
            extractedBrief,
          }))
          .slice(0, isFreestyleGenerationTool(toolPromptCopy.generationTool) ? 3 : 1); // AC supported
      }
      return toolPromptCopy;
    },
};

export const ACTIONS = {
  init:
    (generationTool) =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.init(generationTool));
    },

  getGenerationTool:
    () =>
    ({ getState }) => {
      return getState().generationTool;
    },

  setGenerationTool:
    (generationTool) =>
    ({ setState }) => {
      return setState({ generationTool });
    },

  setChainId:
    (chainId) =>
    ({ setState }) => {
      setState({ chainId });
    },

  setCsvChainId:
    (csvChainId) =>
    ({ setState }) => {
      setState({ csvChainId });
    },

  getToolPrompts:
    () =>
    ({ getState }) => {
      return getState().toolPrompts;
    },

  setToolPrompts:
    (toolPrompts) =>
    ({ setState, dispatch }) => {
      setState({ toolPrompts });
    },

  setToolPrompt:
    (blockId, value) =>
    ({ setState, getState, dispatch }) => {
      const toolPrompts = getState().toolPrompts;
      setState({
        toolPrompts: toolPrompts.map((toolPrompt) => {
          return toolPrompt.blockId === blockId ? value : toolPrompt;
        }),
      });
    },

  updateToolPromptIdByIndex:
    (index, id) =>
    ({ setState, getState }) => {
      const toolPrompts = getState().toolPrompts;
      const updatedToolPrompts = toolPrompts.map((toolPrompt, idx) => {
        if (idx === parseInt(index)) {
          return { ...toolPrompt, blockId: id };
        } else {
          return toolPrompt;
        }
      });
      setState({ toolPrompts: updatedToolPrompts });
    },

  // currently unused, but may be used in the future
  removeBlock:
    (blockId) =>
    ({ setState, getState }) => {
      const toolPrompts = getState().toolPrompts.filter(
        (toolPrompt) => toolPrompt.blockId !== blockId
      );
      setState({ toolPrompts });
      const removedBlocks = getState().removedBlocks;
      setState({ removedBlocks: [...removedBlocks, blockId] });
    },

  getVisibleDDTemplates: (prompt) => () => {
    const availableTemplates = getToolGenerationModes(prompt.generationTool);
    return filterVisibleDDTemplates(availableTemplates);
  },

  waitForMultiPromptRunner:
    ({ chainId, csvChainId, projectId }) =>
    async ({ dispatch }) => {
      const result = await getMultiPromptRunner({ chainId, csvChainId, projectId });
      if (result.length === 0 || result[0].isGenerating) {
        await promisedWait(2000);
        return dispatch(ACTIONS.waitForMultiPromptRunner({ chainId, csvChainId, projectId }));
      } else {
        return result;
      }
    },

  generateVariations:
    ({ projectId, language, chainIdForGeneration, csvChainIdForGeneration, options }) =>
    async ({ getState, dispatch }) => {
      const blocksUpdatedSettings = {};
      let extractedBriefs;
      // Wait for brief extraction if needed
      let briefExtraction;
      if (options?.briefExtractionPromise) {
        briefExtraction = await options?.briefExtractionPromise;
        if (briefExtraction?.blockingErrors) {
          return { results: {}, briefExtractionResult: briefExtraction };
        }

        extractedBriefs = briefExtraction?.extractedBriefs;
      }

      getState().toolPrompts.forEach((toolPrompt) => {
        blocksUpdatedSettings[toolPrompt.blockId] = dispatch(
          PRIVATE_ACTIONS.prepareToolPromptForGeneration(toolPrompt, language, extractedBriefs)
        );
      });
      const chainId = chainIdForGeneration || getState().chainId;
      const csvChainId = csvChainIdForGeneration || getState().csvChainId;
      const removedBlocks = getState().removedBlocks;
      // run multi prompt runner without waiting for the result and start polling
      const multiPromptChainIds = await runMultiPromptRunnerAsync({
        chainId,
        csvChainId,
        blocksUpdatedSettings: JSON.stringify(blocksUpdatedSettings),
        removedBlocks,
      });
      // wait for two seconds to make sure that the generation is started
      await promisedWait(2000);
      const result = await dispatch(
        ACTIONS.waitForMultiPromptRunner({
          chainId: multiPromptChainIds.chainId,
          csvChainId: multiPromptChainIds.csvChainId,
          projectId,
        })
      );

      if (briefExtraction?.errors) {
        return { results: result, briefExtractionResult: briefExtraction };
      }
      return { results: result, briefExtractionResult: null };
    },
};

export const LocalStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'multi-prompt-template-popup',
});
